import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { ExternalLink, LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from '../_marketingPage/marketingPage.module.css';
import pageCss from './ContactPage.module.css';

/**
 * Contact page (`/contact`).
 *
 * Customer-service entry point. Shares the marketing-page chrome
 * (hero / sectioned content) with the legal pages, plus per-page
 * overrides from ContactPage.module.css that implement the May-2026
 * client brief: global center-alignment, tighter vertical rhythm,
 * shorter mobile hero, and no closing kontakt band.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const ContactPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled } = props;

  const supportEmail = 'post@zuprize.no';
  const emailLink = (
    <ExternalLink href={`mailto:${supportEmail}`} className={css.kontaktLink}>
      {supportEmail}
    </ExternalLink>
  );

  return (
    <Page
      title={intl.formatMessage({ id: 'ContactPage.metaTitle' })}
      description={intl.formatMessage({ id: 'ContactPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="ContactPage" />} footer={<FooterContainer />}>
        <article className={css.root}>
          <div className={css.content}>
            <header className={css.hero}>
              <p className={css.heroEyebrow}>
                <FormattedMessage id="ContactPage.heroEyebrow" />
              </p>
              <h1 className={css.heroHeading}>
                <FormattedMessage id="ContactPage.heroHeading" />
              </h1>
              <p className={css.lede}>
                <FormattedMessage id="ContactPage.heroLede" />
              </p>
            </header>

            <section className={css.section}>
              <h2 className={classNames(css.sectionHeading, pageCss.sectionHeading)}>
                <FormattedMessage id="ContactPage.emailHeading" />
              </h2>
              <p className={classNames(css.body, pageCss.body)}>
                <FormattedMessage id="ContactPage.emailBody1" values={{ email: emailLink }} />
              </p>
              <p className={classNames(css.body, pageCss.body)}>
                <FormattedMessage id="ContactPage.emailBody2" />
              </p>
            </section>

            <section className={css.section}>
              <h2 className={classNames(css.sectionHeading, pageCss.sectionHeading)}>
                <FormattedMessage id="ContactPage.helpHeading" />
              </h2>
              <p className={classNames(css.body, pageCss.body)}>
                <FormattedMessage id="ContactPage.helpIntro" />
              </p>
              <ul className={classNames(css.list, pageCss.list)}>
                <li>
                  <FormattedMessage
                    id="ContactPage.helpBullet1"
                    values={{
                      link: chunks => (
                        <NamedLink className={css.kontaktLink} name="UserTermsPage">
                          {chunks}
                        </NamedLink>
                      ),
                    }}
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="ContactPage.helpBullet2"
                    values={{
                      link: chunks => (
                        <NamedLink className={css.kontaktLink} name="UserTermsPage">
                          {chunks}
                        </NamedLink>
                      ),
                    }}
                  />
                </li>
              </ul>
            </section>

            <section className={css.section}>
              <h2 className={classNames(css.sectionHeading, pageCss.sectionHeading)}>
                <FormattedMessage id="ContactPage.companyHeading" />
              </h2>
              <p className={classNames(css.body, pageCss.body)}>
                <FormattedMessage id="ContactPage.companyName" />
                <br />
                <FormattedMessage id="ContactPage.companyOrgNumber" />
                <br />
                {emailLink}
              </p>
            </section>
          </div>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const ContactPage = connect(mapStateToProps)(ContactPageComponent);

export { ContactPageComponent };
export default ContactPage;
